import {useState} from 'react'
import {useQuery, useQueryClient} from '@tanstack/react-query'
import {Server, Box, Cable, Check, X, Plus, Tag, Info, Trash2, Shield} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {usePageTitle} from '@/hooks/use-page-title'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useEntityMutation} from '@/hooks/useEntityMutation'
import {Button} from '@/components/ui/button'
import {DeleteConfirmDialog} from '@/components/shared/DeleteConfirmDialog'
import {Card, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge} from '@/components/ui/badge'
import {DetailsCard} from '@/components/shared/DetailsCard'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {routes} from '@/lib/routes'
import {Alert, AlertDescription} from '@/components/ui/alert'
import {useAuth} from '@/contexts/auth-context'
import {permissions} from '@/lib/permissions'
import {TerminationAction} from '@/types/system'
import {OutboundConnectorType} from '@/types/outbound-connector'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {formatTimestamp} from '@/lib/date-utils'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {OwnersSection, OwnersEntityType} from '@/components/owners/OwnersSection'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {Icon} from '@/components/shared/Icon'
import {PageLayout} from '@/components/shared/PageLayout'
import {RowCardHeader} from '@/components/shared/RowCardHeader'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {CardSection} from '@/components/shared/CardSection'
import {IconText} from '@/components/shared/IconText'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {DetailField} from '@/components/shared/DetailField'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {resolveTypeValueIcon} from '@/components/entity-type/entity-type-icon'

export function SystemDetail() {
    const services = useServices()
    const i18n = useI18n()
    usePageTitle(i18n.systems.detailPageTitle)
    const navigate = useNavigate()
    const {id} = useParams<{ id: string }>()
    const {hasPermission} = useAuth()

    const canViewConnectors = hasPermission(permissions.outboundConnectors.view)
    const canCreateConnectors = hasPermission(permissions.outboundConnectors.create)
    const canEditAttributes = hasPermission(permissions.systems.view)
    const canUpdate = hasPermission(permissions.systems.update)
    const canDelete = hasPermission(permissions.systems.delete)
    const [deleteOpen, setDeleteOpen] = useState(false)
    const queryClient = useQueryClient()

    const deleteMutation = useEntityMutation({
        mutationFn: () => services.systems.remove!(id!),
        successMessage: i18n.systems.deleteSuccess,
        onSuccess: () => {
            setDeleteOpen(false)
            queryClient.invalidateQueries({queryKey: ['systems']})
            navigate(routes.systems.list())
        },
    })

    // Fetch system
    const {data: system, isLoading, error} = useQuery({
        queryKey: ['system', id],
        queryFn: () => services.systems.detail(id!),
        enabled: !!id,
    })

    // Fetch resources for this system
    const {data: resourcesData} = useQuery({
        queryKey: ['resources', 'by-system', id],
        queryFn: () => services.resources.bySystem(id!),
        enabled: !!id,
    })
    const resources = resourcesData?.content ?? []

    // Fetch enforcement objects for this system
    const {data: enforcementObjectsData} = useQuery({
        queryKey: ['enforcement-objects', 'by-system', id],
        queryFn: () => services.enforcementObjects.bySystem(id!),
        enabled: !!id,
    })
    const enforcementObjects = enforcementObjectsData || []

    // Fetch connector for this system
    const {data: connector, isLoading: connectorLoading} = useQuery({
        queryKey: ['connector', 'by-system', id],
        queryFn: () => services.outboundConnectors.bySystem(id!).catch(() => null),
        enabled: !!id && canViewConnectors,
    })

    const {systemTypes, environmentTypes, resourceTypes, enforcementTypes, targetSystemResponsibilityTypes} = useAppConfig()

    const getOutboundConnectorTypeLabel = (type: string) => {
        switch (type) {
            case OutboundConnectorType.SCIM_2_0:

                return 'SCIM 2.0'
            default:
                return type
        }
    }

    const s = system

    const HeaderIcon = resolveTypeValueIcon(systemTypes, system?.systemType, Server)

    return (
        <PageLayout
            data-testid="system-detail-page"
            variant="detail"
            title={system?.name ?? ''}
            icon={HeaderIcon}
            backTo={routes.systems.list()}
            loading={isLoading}
            error={error || (!isLoading && !system ? new Error('System not found') : undefined)}
            actions={system ? (
                <>
                    <Badge variant="outline">
                        {labelOf(systemTypes, system.systemType)}
                    </Badge>
                    <Badge variant="outline">
                        {labelOf(environmentTypes, system.environment)}
                    </Badge>
                    {canDelete && (
                        <Button
                            data-testid="btn-delete-system"
                            size="sm"
                            variant="destructive"
                            onClick={() => setDeleteOpen(true)}
                        >
                            <Icon icon={Trash2} className="mr-1"/>
                            {i18n.systems.deleteSystem}
                        </Button>
                    )}
                </>
            ) : undefined}
        >
            {s && (<>
                <DeleteConfirmDialog
                    open={deleteOpen}
                    onOpenChange={setDeleteOpen}
                    title={i18n.systems.deleteConfirmTitle}
                    description={i18n.systems.deleteConfirmDescription({name: s.name})}
                    testIdPrefix="delete-system"
                    isPending={deleteMutation.isPending}
                    onConfirm={() => deleteMutation.mutate(undefined)}
                />

                <DetailsCard
                    data-testid="card-system-details"
                    title={i18n.systems.systemDetails}
                    description={i18n.systems.configDescription}
                    fields={[
                        {label: i18n.systems.systemType, value: labelOf(systemTypes, s.systemType)},
                        {label: i18n.systems.environment, value: labelOf(environmentTypes, s.environment)},
                        {
                            label: i18n.systems.authoritativeForIdentities,
                            value: s.authoritativeForIdentities
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        {
                            label: i18n.systems.authoritativeForAttributes,
                            value: s.authoritativeForAttributes
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        {
                            label: i18n.systems.authoritativeForAccounts,
                            value: s.authoritativeForAccounts
                                ? <IconText icon={Check} iconClassName="text-status-success"><span>{i18n.common.yes}</span></IconText>
                                : <IconText icon={X} iconClassName="text-muted-foreground"><span>{i18n.common.no}</span></IconText>,
                        },
                        {
                            label: i18n.systems.terminationAction,
                            value: <Badge data-testid="termination-action-label" variant={s.terminationAction === TerminationAction.DELETE ? 'destructive' : 'warning'}>
                                {s.terminationAction === TerminationAction.DELETE
                                    ? i18n.systems.terminationActionDelete
                                    : i18n.systems.terminationActionDisable}
                            </Badge>,
                        },
                        {label: i18n.common.createdAt, value: formatTimestamp(s.createdAt)},
                        {label: i18n.common.lastUpdated, value: formatTimestamp(s.updatedAt)},
                    ]}
                />

                {connector && ![OutboundConnectorType.SCIM_2_0, OutboundConnectorType.LDAP].includes(connector.connectorType as typeof OutboundConnectorType.SCIM_2_0) && (
                    <Alert data-testid="connector-capability-banner">
                        <Icon icon={Info}/>
                        <AlertDescription>
                            {i18n.systems.connectorCapabilityBanner({type: getOutboundConnectorTypeLabel(connector.connectorType)})}
                        </AlertDescription>
                    </Alert>
                )}

                <CardSection data-testid="section-attributes" icon={Tag} title={i18n.common.attributes}>
                    <InlineAttributeEditor
                        entityType={AttributeEntityType.TARGET_SYSTEM}
                        entityId={id!}
                        entityTypeCode={s.systemType}
                        canEdit={canEditAttributes}
                    />
                </CardSection>

                <OwnersSection
                    entityType={OwnersEntityType.systems}
                    entityId={id!}
                    canEdit={canUpdate}
                    responsibilityOptions={targetSystemResponsibilityTypes}
                />

                {/* Connector */}
                {canViewConnectors && (
                    <Card>
                        <RowCardHeader>
                            <div>
                                <CardTitle data-testid="card-title-connector">
                                    <IconText icon={Cable} size="md">{i18n.systems.connector}</IconText>
                                </CardTitle>
                                <CardDescription>
                                    {connector ? i18n.systems.connectorDescription : i18n.systems.noConnectorConfigured}
                                </CardDescription>
                            </div>
                            {!connector && !connectorLoading && canCreateConnectors && (
                                <Button data-testid="btn-add-connector" size="sm"
                                        onClick={() => navigate(routes.connectors.outbound.createForSystem(id!))}>
                                    <IconText icon={Plus}>{i18n.systems.addConnector}</IconText>
                                </Button>
                            )}
                        </RowCardHeader>
                        <CardContent>
                            {connectorLoading ? (
                                <MutedText>{i18n.systems.loadingConnector}</MutedText>
                            ) : connector ? (
                                <DetailGrid columns={3}>
                                    <DetailField
                                        label={i18n.common.type}
                                        value={<Badge variant="outline">{getOutboundConnectorTypeLabel(connector.connectorType)}</Badge>}
                                    />
                                    <DetailField
                                        label={i18n.common.status}
                                        value={connector.enabled ? (
                                            <IconText icon={Check} iconClassName="text-status-success">{i18n.common.enabled}</IconText>
                                        ) : (
                                            <IconText icon={X} iconClassName="text-muted-foreground">{i18n.common.disabled}</IconText>
                                        )}
                                    />
                                    <DetailField
                                        label={i18n.common.details}
                                        value={
                                            <TextLink
                                                to={routes.connectors.outbound.detail(connector.id)}
                                                testId="link-connector-detail"
                                            >
                                                {i18n.systems.viewConnector}
                                            </TextLink>
                                        }
                                    />
                                </DetailGrid>
                            ) : (
                                <InlineEmptyText>{i18n.systems.noConnector}</InlineEmptyText>
                            )}
                        </CardContent>
                    </Card>
                )}

                {/* Resources */}
                <CardSection data-testid="section-resources" icon={Box} title={i18n.resources.title} description={i18n.systems.resourceCount({count: resources.length})}>
                    {resources.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                                    <TableHead data-testid="col-classification">{i18n.systems.classification}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {resources.map((resource) => (
                                    <ClickableTableRow
                                        key={resource.id}
                                        to={routes.systems.resources.detail(resource.id)}
                                    >
                                        <TableCell>
                                            <TextLink
                                                to={routes.systems.resources.detail(resource.id)}
                                                testId={`link-resource-detail-${resource.id}`}
                                                stopPropagation
                                                variant="bold"
                                            >
                                                {resource.derivedDisplayName}
                                            </TextLink>
                                            <MutedText size="sm">{resource.nativeKey}</MutedText>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(resourceTypes, resource.resourceType)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="secondary">{resource.dataClassification}</Badge>
                                        </TableCell>
                                    </ClickableTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <InlineEmptyText>{i18n.systems.noResources}</InlineEmptyText>
                    )}
                </CardSection>

                {/* Enforcement Objects */}
                <CardSection
                    data-testid="section-enforcement-objects"
                    icon={Shield}
                    title={i18n.enforcementObjects.title}
                    description={i18n.systems.enforcementObjectCount({count: enforcementObjects.length})}
                >
                    {enforcementObjects.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-eo-name">{i18n.common.name}</TableHead>
                                    <TableHead data-testid="col-eo-type">{i18n.common.type}</TableHead>
                                    <TableHead data-testid="col-eo-resource">{i18n.resources.title}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {enforcementObjects.map((eo) => (
                                    <ClickableTableRow
                                        key={eo.id}
                                        to={routes.systems.enforcementObjects.detail(eo.id)}
                                    >
                                        <TableCell>
                                            <TextLink
                                                to={routes.systems.enforcementObjects.detail(eo.id)}
                                                testId={`link-enforcement-object-${eo.id}`}
                                                stopPropagation
                                                variant="bold"
                                            >
                                                {eo.derivedDisplayName}
                                            </TextLink>
                                            <MutedText size="sm">{eo.externalIdentifier}</MutedText>
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant="outline">
                                                {labelOf(enforcementTypes, eo.enforcementType)}
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            {eo.resource ? (
                                                <EntityLink
                                                    value={eo.resource}
                                                    stopPropagation
                                                    testId={`link-enforcement-object-resource-${eo.id}`}
                                                />
                                            ) : (
                                                <MutedText size="sm">—</MutedText>
                                            )}
                                        </TableCell>
                                    </ClickableTableRow>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <InlineEmptyText>{i18n.systems.noEnforcementObjects}</InlineEmptyText>
                    )}
                </CardSection>
            </>)}
        </PageLayout>
    )
}
